import { ApolloProvider } from '@apollo/client';
import { useSessionStorageState } from 'ahooks';
import { useEffect } from 'react';
import TagManager from 'react-gtm-module';
import {
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from 'react-router-dom';
import { FirebaseUserGuard } from './components/guards/FirebaseUserGuard';
import { OrganizationAccessGuard } from './components/guards/OrganizationAccessGuard';
import { OrganizationLockGuard } from './components/guards/OrganizationLockGuard';
import { LoggedLayout } from './components/LoggedLayout';
import { OnboardingLayout } from './components/OnboardingLayout';
import { UnloggedLayout } from './components/UnloggedLayout';
import { FirebaseAuthProvider } from './contexts/AuthContext/FirebaseAuthContext';
import { EnvironmentProvider } from './contexts/EnvironmentContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { AudioProvider } from './contexts/PlayRecordingContext';
import { RightHandSidebarProvider } from './contexts/RightHandSidebarContext';
import { ServiceModeProvider } from './contexts/ServiceModeContext';
import { client } from './graphql/client';
import {
  Automations,
  Billing,
  CallAutomationWizard,
  Calls,
  ScheduledCalls,
  ChangeOrganization,
  Dashboard,
  ListAccountConnections,
  ListActions,
  ListAgents,
  ListIntegrationAppConnectionsProvider,
  ListPrompts,
  ListVoices,
  ListWebhooks,
  Login,
  ManageDnc,
  ManageNumbers,
  ManagePrompt,
  NotFound,
  Onboarding,
  Profile,
  ResetPassword,
  Settings,
  SignUp,
  OrgSwitch,
} from './pages';
import { ManageAgentProvider as ManageAgent } from './pages/Agents/ManageAgentProvider';
import ContactList from './pages/Automations/ContactList';

import AddContactsFromCrm from './pages/Automations/AddContactsFromCrm';
import { AddContactsFromCsv } from './pages/Automations/AddContactsFromCsv';
import { useAnalytics } from './hooks/useAnalytics';
import { ChatAgent } from './pages/Agents/ChatAgent';
import { NavigationProvider } from 'src/contexts/NavigationContext';
import ContactWindow from './pages/Automations/beta/ContactWindow/index';
import { Automations as BetaAutomations } from './pages/Automations/beta/Automations';
import CampaignOverview from './pages/Automations/beta/CampaignOverview';
import CampaignWizardLayout from './pages/Automations/beta/CampaignWizardLayout';
import { ConversationEditor } from './pages/ConversationEditor';
import { Organizations } from './pages/Organizations';
import { ListKnowledgeBases } from './pages/KnowledgeBases/ListKnowledgeBases';
import { ManageKnowledgeBase } from './pages/KnowledgeBases/ManageKnowledgeBase';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { Messages } from './pages/Messages';
import { ListContactWindows } from './pages/Automations/beta/ContactWindow/ListContactWindows';
import { TrainingCenter } from './pages/TrainingCenter';
import { DataMapping } from './pages/DataMapping';
import { IntegrationDataMapping } from './pages/DataMapping/IntegrationDataMapping';

const AppRoutes = () => {
  const location = useLocation();
  const [, setFilters] = useSessionStorageState(
    '@control-plane/agent-filters',
    {
      defaultValue: {
        source: '',
        name: '',
        page: 1,
        size: 10,
        sort_column: undefined,
        sort_desc: undefined,
      },
    },
  );
  const { track } = useAnalytics();

  useEffect(() => {
    const tagManagerArgs = {
      gtmId: String(process.env.REACT_APP_GTM_ID),
    };

    TagManager.initialize(tagManagerArgs);
  }, []);

  useEffect(() => {
    const isAgentRoute =
      location.pathname.startsWith('/agent/') ||
      location.pathname.startsWith('/agents');

    if (!isAgentRoute) {
      setFilters({
        source: '',
        name: '',
        page: 1,
        size: 10,
        sort_column: undefined,
        sort_desc: undefined,
      });
    }

    track.pageViewed({
      path: location.pathname,
      title: location.pathname,
      referrer: location.state?.referrer,
    });
  }, [location.pathname, location.state?.referrer, track.pageViewed]);

  return (
    <Routes>
      <Route element={<FirebaseUserGuard />}>
        <Route element={<LoggedLayout />}>
          <Route path="/settings/organizations" element={<Organizations />} />
          <Route path="/profile" element={<Profile />} />
          <Route element={<OrganizationAccessGuard />}>
            <Route element={<OrganizationLockGuard />}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/agents" element={<ListAgents />} />
              <Route path="/agents/chat/new" element={<ChatAgent />} />
              <Route path="/agent/new" element={<ManageAgent />} />
              <Route
                path="/agent/edit/:id"
                element={<ManageAgent editing={true} />}
              />
              <Route path="/calls" element={<Calls />} />
              <Route path="/calls/scheduled" element={<ScheduledCalls />} />
              <Route
                path="/calls/:id/conversation-editor"
                element={<ConversationEditor />}
              />
              <Route path="/prompts" element={<ListPrompts />} />
              <Route path="/prompt/create" element={<ManagePrompt />} />
              <Route
                path="/prompt/edit/:id"
                element={<ManagePrompt editing={true} />}
              />
              <Route path="/numbers" element={<ManageNumbers />} />
              <Route path="/dnc-numbers" element={<ManageDnc />} />
              <Route path="/voices" element={<ListVoices />} />
              <Route path="/providers" element={<ListAccountConnections />} />
              <Route path="/actions" element={<ListActions />} />
              <Route path="/webhooks" element={<ListWebhooks />} />
              <Route path="/knowledge-bases" element={<ListKnowledgeBases />} />
              <Route
                path="/knowledge-bases/:id"
                element={<ManageKnowledgeBase />}
              />
              <Route
                path="/integration-app"
                element={<ListIntegrationAppConnectionsProvider />}
              />
              <Route path="/campaigns" element={<Automations />} />
              <Route
                path="/campaigns/create"
                element={<CallAutomationWizard />}
              />
              <Route
                path="/campaigns/edit/:id"
                element={<CallAutomationWizard editing={true} />}
              />
              <Route path="/campaigns/:id/contacts" element={<ContactList />} />
              <Route
                path="/campaigns/:id/contacts/csv"
                element={<AddContactsFromCsv />}
              />
              <Route
                path="/campaigns/:id/contacts/crm"
                element={<AddContactsFromCrm />}
              />
              <Route path="/messages" element={<Messages />} />
              <Route path="/data-mapping" element={<DataMapping />} />
              <Route
                path="/data-mapping/:integrationId"
                element={<IntegrationDataMapping />}
              />
              {/* Start beta routes */}
              <Route path="/beta/campaigns" element={<BetaAutomations />} />
              <Route
                path="/beta/campaigns/contact-windows"
                element={<ListContactWindows />}
              />
              <Route
                path="/beta/campaigns/new"
                element={<CampaignWizardLayout />}
              />
              <Route
                path="/beta/campaigns/contact-window"
                element={<ContactWindow />}
              />
              <Route path="/beta/campaigns" element={<BetaAutomations />} />
              <Route
                path="/beta/campaigns/overview/:id"
                element={<CampaignOverview />}
              />
              <Route
                path="/beta/campaigns/:id/contacts/csv"
                element={<AddContactsFromCsv />}
              />
              <Route
                path="/beta/campaigns/:id/contacts/crm"
                element={<AddContactsFromCrm />}
              />
              <Route
                path="/beta/campaigns/contact-window"
                element={<ContactWindow />}
              />
              <Route path="/beta/campaigns" element={<BetaAutomations />} />
              <Route
                path="/beta/campaigns/overview/:id"
                element={<CampaignOverview />}
              />

              <Route
                path="/beta/conversation-editor"
                element={<ConversationEditor />}
              />

              <Route
                path="/beta/training-center"
                element={<TrainingCenter />}
              />
              {/* End beta routes */}
            </Route>
          </Route>
          <Route path="/billing" element={<Billing />} />
          <Route path="/change-organization" element={<ChangeOrganization />} />
        </Route>

        <Route element={<OnboardingLayout />}>
          <Route path="/onboarding" element={<Onboarding />} />
        </Route>
        {/* Spinner route outside OrganizationLockGuard to avoid org-locked API calls during switch */}
        <Route path="/org-switch" element={<OrgSwitch />} />
      </Route>

      <Route
        element={
          <GoogleReCaptchaProvider
            reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY!}
            useEnterprise
            scriptProps={{ async: true, defer: true }}
          >
            <UnloggedLayout />
          </GoogleReCaptchaProvider>
        }
      >
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
};

export const AllRoutes = () => {
  return (
    <Router>
      <FirebaseAuthProvider>
        <ApolloProvider client={client}>
          <NotificationProvider>
            <NavigationProvider>
              <EnvironmentProvider>
                <ServiceModeProvider>
                  <RightHandSidebarProvider>
                    <AudioProvider>
                      <AppRoutes />
                    </AudioProvider>
                  </RightHandSidebarProvider>
                </ServiceModeProvider>
              </EnvironmentProvider>
            </NavigationProvider>
          </NotificationProvider>
        </ApolloProvider>
      </FirebaseAuthProvider>
    </Router>
  );
};
